/**
 * Core / Mint: Mint Components
 * ------------------------------------------------------------------------------
 * - Component for initiating the standalone components
 * - Automatically creates Vue instance for any Vue element with the
 *   data attribute `data-mint-component="name"` and loads in relevant
 *   plugins like Pinia.
 * - This is for standalone Vue components, not imported components
 *   within an existing Vue component.
 * @example:
 * - If this file exists:
 *  `src/vue/components/test-component/test-component.vue`:
 * - Add the below in HTML and it will mount:
 *
    <div data-mint-component="test-component">
      <test-component
        :test="`hello, world`"
        v-cloak
      ></test-component>
    </div>
 *
 * @namespace MintComponents
 */
import { createApp } from 'vue'

import * as Mint from '~/core/mint/mint-setup'
import CoreComputed from '~/core/mint/methods/core-computed'
import SectionMethods from '~/core/mint/methods/section-methods'

import { importComponent } from '~/core/mint/helpers'

import { handleToPascalCase } from '~/helpers/strings'
import { storageNamespace } from '~/helpers/storage-namespaces'

/**
 * Attributes.
 */
const attributes = {
  component: 'data-mint-component',
}

/**
 * Create a new MintComponents object.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    components: [...document.querySelectorAll(`[${attributes.component}]`)],
  }

  /**
   * Initialise component.
   */
  function init() {

    /**
     * Prevent execution if no components are present
     * and the user is not in Customiser, stop.
     */
    if (!Shopify.designMode && !nodeSelectors.components.length) {
      return
    }

    nodeSelectors.components.forEach((component, index) => {
      setupComponent(component, index)
    })
  }

  /**
   * Set up Vue components
   * - Get the handle from the attribute
   * - Add additional Vue plugins, mixins etc.
   * - Mount
   * @param {HTMLElement} component - HTML element for the component.
   */
  function setupComponent(component) {
    const componentHandle = component.getAttribute(attributes.component)

    /**
     * Create Vue app instance.
     */
    const app = getApp(componentHandle)

    /**
     * Add setup options.
     */
    Mint.setDirectives(app)
    Mint.setPlugins(app)

    app.mount(component)
  }

  /**
   * Return a Vue app creation object.
   * @param {String} handle - A given handle for a Vue component.
   * @returns {Object}
   */
  function getApp(componentHandle) {
    const components = {}
    components[componentHandle] = importComponent(componentHandle)

    /**
     * Vue returns 'Maximum Call Stack Error' if the name is *only* the
     * returned function string. One to investigate.
     */
    const PascalName = handleToPascalCase(componentHandle)
    const name = `${PascalName}Component`

    return createApp({
      name,
      delimiters: ['${', '}'],
      components,
      computed: {
        ...CoreComputed(),
      },
      mounted() {
        this.setEventBusListeners()

        /**
         * For API based shared-stores, retrieve a cached version
         * and set the data.
         * - Mint Core will have performed API call on page load and cached in browser.
         * - As other sections mount, get the data and set in section stores.
         * - Any subsequent API update will be handled and updated via EventBus.
         */
        const cachedCart = JSON.parse(
          sessionStorage.getItem(storageNamespace.pinia.cart),
        )

        const cachedLocale = JSON.parse(
          sessionStorage.getItem(storageNamespace.pinia.locale),
        )

        const cachedWishlist = JSON.parse(
          sessionStorage.getItem(storageNamespace.pinia.wishlist),
        )

        if (cachedCart) {
          this.setCart(cachedCart)
        }

        if (cachedLocale) {
          this.setLocale(cachedLocale, true)
        }

        if (cachedWishlist) {
          this.setWishlist(cachedWishlist)
        }

        /**
         * Execute Pinia functions on mount.
         * - Any method call here should also be added:
         * - mint-components.js
         * - mint-core.js
         * - mint-section.js
         * - Ensures that Pinia state is shared between all
         *   Vue instances.
         */
        this.setFeatureFlags()
        this.handleResize()
        this.setWishlist()
      },
      methods: SectionMethods(),
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
